/**
 * Dialog Hook
 *
 * A convenience hook that provides access to the dialog context throughout the application.
 * This hook simplifies access to dialog functionality without needing to directly
 * import and use the context consumer.
 *
 * Features:
 * - Simplified access to all dialog methods
 * - Consistent dialog API across components
 * - Typed return values for better developer experience
 *
 * Usage example:
 * ```jsx
 * const { showAlertDialogBox, showConfirmationDialogBox } = useDialog();
 *
 * // Show a simple alert
 * const handleShowAlert = () => {
 *   showAlertDialogBox({
 *     title: "Information",
 *     message: "Operation completed successfully",
 *     dialogType: "success"
 *   });
 * };
 * ```
 *
 * @module hooks/useDialog
 */

import { useContext } from "react";
import { DialogContext } from "../context/dialog-context";

/**
 * Hook to access dialog functionality throughout the application
 *
 * @returns {Object} Dialog context with methods for showing different dialog types
 */
export const useDialog = () => useContext(DialogContext);
